import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { AzureDataLakeStoreLinkedService } from 'app/model/linkedservice/azure-data-lake-store.linkedservice';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from './azure-key-vault.util';
import { CredentialTypeOptions, Credential, azureDefaultAuthRefTypes, azureCloudTypeDef } from 'shell-models-lib';
import { ReferenceAdapter } from '../reference.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDataLakeStoreLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                dataLakeStoreUri: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                tenant: '*',
                accountName: '*',
                subscriptionId: '*',
                resourceGroupName: '*',
                encryptedCredential: '*',
                azureCloudType: '*',
                credential: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureDataLakeStoreLinkedServiceAdapter extends LinkedServiceAdapter<AzureDataLakeStoreLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureDataLakeStoreLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationType;
        if (model.credential) {
            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = AuthenticationType.ServicePrincipal;
            } else if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = AuthenticationType.UserAuth;
            } else if (credType === CredentialTypeOptions.ManagedIdentity) {
                finalAuthType = AuthenticationType.ManagedServiceIdentity;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = AuthenticationType.UserAssignedManagedIdentity;
            } else {
                finalAuthType = model.authenticationType;
            }
        }
        model.authenticationType = finalAuthType;
    }

    protected modelConstructor(): new () => AzureDataLakeStoreLinkedService {
        return AzureDataLakeStoreLinkedService;
    }

    protected model(): IModel {
        return AzureDataLakeStoreLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureDataLakeStoreLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureDataLakeStoreLinkedService;

        properties.type = Metamodel.modelFor(AzureDataLakeStoreLinkedService).name;

        let servicePrincipalId: string;
        let servicePrincipalKey: string | Model.SecretBase;
        let azureCloudType: azureCloudTypeDef;
        if (linkedService.authenticationType === AuthenticationType.ServicePrincipal && linkedService.authReferenceMethod === azureDefaultAuthRefTypes.InlineRefTypeAuthentication) {
            azureCloudType = linkedService.azureCloudType;
            servicePrincipalId = this.valueToResource(linkedService, AzureDataLakeStoreLinkedService.SERVICE_PRINCIPAL_ID);
            servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureDataLakeStoreLinkedService.SERVICE_PRINCIPAL_KEY, this.context);
        }
        properties.typeProperties = {
            dataLakeStoreUri: this.valueToResource(linkedService, AzureDataLakeStoreLinkedService.DATA_LAKE_STORE_URL),
            azureCloudType,
            servicePrincipalId,
            servicePrincipalKey,
            tenant: this.valueToResource(linkedService, AzureDataLakeStoreLinkedService.TENANT),
            subscriptionId: this.valueToResource(linkedService, AzureDataLakeStoreLinkedService.SUBSCRIPTION_ID),
            resourceGroupName: this.valueToResource(linkedService, AzureDataLakeStoreLinkedService.RESOURCE_GROUP_NAME),
            credential: (linkedService.credential &&
                ((linkedService.authenticationType === AuthenticationType.ServicePrincipal &&
                    linkedService.authReferenceMethod === azureDefaultAuthRefTypes.CredentialRefTypeAuthentication)
                    || (linkedService.authenticationType === AuthenticationType.ManagedServiceIdentity &&
                        linkedService.authReferenceMethod === azureDefaultAuthRefTypes.CredentialRefTypeAuthentication)
                    || linkedService.authenticationType === AuthenticationType.UserAssignedManagedIdentity
                    || linkedService.authenticationType === AuthenticationType.UserAuth)) ?
                ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference : undefined
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureDataLakeStoreLinkedService): Promise<AzureDataLakeStoreLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureDataLakeStoreLinkedService;
        linkedService.authReferenceMethod = properties.typeProperties.credential ? azureDefaultAuthRefTypes.CredentialRefTypeAuthentication : azureDefaultAuthRefTypes.InlineRefTypeAuthentication;
        linkedService.credential = undefined;

        if (properties.typeProperties.servicePrincipalId) {
            ValueAdapter.toModel(linkedService, AzureDataLakeStoreLinkedService.SERVICE_PRINCIPAL_ID, properties.typeProperties.servicePrincipalId);
            linkedService.authenticationType = AuthenticationType.ServicePrincipal;
        } else if (properties.typeProperties.credential) {
            // postToModel will handle authenticationType
            linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), properties.typeProperties.credential, this.context);
        } else {
            // MSI has no credential
            linkedService.authenticationType = AuthenticationType.ManagedServiceIdentity;
        }

        parameterizableSecretBaseToModel(linkedService, AzureDataLakeStoreLinkedService.SERVICE_PRINCIPAL_KEY, properties.typeProperties.servicePrincipalKey, this.context);
        ValueAdapter.toModel(linkedService, AzureDataLakeStoreLinkedService.DATA_LAKE_STORE_URL, properties.typeProperties.dataLakeStoreUri);
        ValueAdapter.toModel(linkedService, AzureDataLakeStoreLinkedService.TENANT, properties.typeProperties.tenant);
        ValueAdapter.toModel(linkedService, AzureDataLakeStoreLinkedService.SUBSCRIPTION_ID, properties.typeProperties.subscriptionId);
        ValueAdapter.toModel(linkedService, AzureDataLakeStoreLinkedService.RESOURCE_GROUP_NAME, properties.typeProperties.resourceGroupName);

        linkedService.azureCloudType = properties.typeProperties.azureCloudType;

        return linkedService;
    }
}
